import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { SerieLayerStateItem } from '@/api/generated/schemas';
import { LabelBadge } from '@/shared/ui';
import { SERIE_STATE_TONE, toSerieLayerKey, toSerieStateKey } from './serieLayers';

interface SerieLayerPipelineProps {
  layers: SerieLayerStateItem[];
  /** Akce zarovnané k pravému okraji řádku vrstev (např. tlačítko Publikovat na detailu seriálu). */
  actions?: ReactNode;
}

/**
 * Pipeline vrstev seriálu (schválená varianta „chips“) – řádek vrstev, kde každá vrstva je LabelBadge
 * (název vrstvy + vnořený stavový badge) a mezi nimi jsou šipky.
 */
export function SerieLayerPipeline({ layers, actions }: SerieLayerPipelineProps) {
  const { t } = useTranslation('venua-planner');
  const ordered = [...layers].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="gap-x-phx-3_5 gap-y-phx-2_5 mt-[11px] flex flex-wrap items-center pb-[9px]">
      <span className="text-phx-text-muted text-phx-3xs font-semibold tracking-[0.06em] uppercase">
        {t('serieDetail.layers.label')}
      </span>
      {ordered.map((layer, index) => {
        const stateKey = toSerieStateKey(layer.state);
        return (
          <Fragment key={layer.layer}>
            {index > 0 ? <span className="text-phx-text-faint text-phx-xs">→</span> : null}
            <LabelBadge
              tone={SERIE_STATE_TONE[stateKey]}
              label={t(`serieDetail.layers.${toSerieLayerKey(layer.layer)}`)}
              status={t(`serieDetail.states.${stateKey}`)}
            />
          </Fragment>
        );
      })}
      {actions ? <span className="gap-phx-2 ml-auto flex items-center">{actions}</span> : null}
    </div>
  );
}
